// Bespoke voxel prefabs (divine and dark knights). Split out of creatures_extra.ts, whose
// CREATURE_PREFABS_EXTRA registry still assembles them.

import { box } from './creatures_extra_shared.js';
import type { CharacterPrefab } from './types.js';

// ─── Seraphim (seraphim) ────────────────────────────────────
// Winged divine humanoid: white/gold robes, golden wings expanded behind the
// shoulders, thin yellow halo disc floating above the head.
export const seraphim: CharacterPrefab = {
  name: 'Seraphim',
  description: 'Winged divine humanoid with a golden halo',
  maxHp: 35,
  parts: [
    {
      name: 'head',
      voxels: box(-1, 11, -1, 3, 3, 3, '#f4e9a1'),
    },
    {
      name: 'hair',
      voxels: box(-1, 14, -1, 3, 1, 3, '#d4a04a'),
    },
    {
      name: 'halo',
      // Thin yellow disc floating above the head — emissive gold
      voxels: [
        ...box(-2, 16, -1, 5, 1, 3, '#ffe066', { emissive: '#ffaa00' }),
        { x: 0, y: 17, z: 0, color: '#ffe066', emissive: '#ffaa00' },
      ],
    },
    {
      name: 'robe',
      voxels: [
        // White robe with gold trim
        ...box(-2, 0, -2, 5, 8, 5, '#f4e9a1'),
        // Gold collar
        ...box(-2, 7, -1, 5, 1, 3, '#d4a04a'),
        // Gold hem
        ...box(-2, 0, -2, 5, 1, 5, '#d4a04a'),
        // Sun symbol on chest
        { x: 0, y: 5, z: 2, color: '#d4a04a', emissive: '#aa7700' },
      ],
    },
    {
      name: 'wings',
      // Expanded box voxels behind the shoulders — spans ~7 wide
      voxels: [
        // Left wing
        ...box(-5, 7, -2, 3, 4, 1, '#f4e9a1'),
        ...box(-5, 8, -1, 2, 2, 1, '#d4a04a'),
        // Right wing
        ...box(3, 7, -2, 3, 4, 1, '#f4e9a1'),
        ...box(4, 8, -1, 2, 2, 1, '#d4a04a'),
        // Wing-tips highlighted gold
        { x: -5, y: 10, z: -2, color: '#ffe066' },
        { x: 5, y: 10, z: -2, color: '#ffe066' },
      ],
    },
    {
      name: 'left_arm',
      voxels: [
        ...box(-3, 6, 0, 1, 5, 1, '#f4e9a1'),
        // Pale hand
        { x: -3, y: 11, z: 0, color: '#f4e9a1' },
      ],
    },
    {
      name: 'right_arm',
      voxels: [
        ...box(3, 6, 0, 1, 5, 1, '#f4e9a1'),
        // Pale hand
        { x: 3, y: 11, z: 0, color: '#f4e9a1' },
      ],
    },
  ],
};

// ─── Luminarch (luminarch) ──────────────────────────────────
// Winged divine humanoid (martial variant): golden armor plates over robes,
// larger wings, raised sword. Taller and more imposing than the seraphim.
export const luminarch: CharacterPrefab = {
  name: 'Luminarch',
  description: 'Winged divine warrior in golden armor with a raised sword',
  maxHp: 45,
  parts: [
    {
      name: 'head',
      voxels: box(-1, 11, -1, 3, 3, 3, '#f4e9a1'),
    },
    {
      name: 'helm_gilded',
      voxels: [
        ...box(-2, 13, -2, 5, 2, 5, '#c8b142', { metalness: 1.0, roughness: 0.3 }),
        ...box(-1, 15, -1, 3, 1, 3, '#c8b142', { metalness: 1.0, roughness: 0.3 }),
        // Gold trim
        { x: -2, y: 14, z: 2, color: '#d4af37' },
        { x: 2, y: 14, z: 2, color: '#d4af37' },
      ],
    },
    {
      name: 'halo_crown',
      voxels: [
        // Radiant crown of light
        ...box(-2, 16, -1, 5, 1, 3, '#ffe066', { emissive: '#ffaa00' }),
        { x: 0, y: 17, z: 0, color: '#ffe066', emissive: '#ffaa00' },
      ],
    },
    {
      name: 'torso_armor',
      voxels: [
        // Golden breastplate
        ...box(-2, 6, -1, 5, 6, 3, '#c8b142', { metalness: 1.0, roughness: 0.3 }),
        // Darker bronze plate detail
        ...box(-1, 7, 1, 3, 4, 1, '#8a7a3a'),
        // Sun emblem centered on chest
        { x: 0, y: 9, z: 2, color: '#ffe066', emissive: '#ffaa00' },
      ],
    },
    {
      name: 'robe_skirt',
      // Robe peeking out below the breastplate
      voxels: [...box(-2, 0, -1, 5, 4, 3, '#f4e9a1'), ...box(-2, 0, -2, 5, 1, 1, '#d4a04a')],
    },
    {
      name: 'wings_large',
      // Larger wings than the seraphim — span ~7 wide, taller
      voxels: [
        // Left wing
        ...box(-5, 6, -2, 3, 6, 1, '#f4e9a1'),
        ...box(-5, 7, -1, 2, 4, 1, '#c8b142'),
        // Right wing
        ...box(3, 6, -2, 3, 6, 1, '#f4e9a1'),
        ...box(4, 7, -1, 2, 4, 1, '#c8b142'),
        // Bright wing-tips
        { x: -5, y: 11, z: -2, color: '#ffe066', emissive: '#ffaa00' },
        { x: 5, y: 11, z: -2, color: '#ffe066', emissive: '#ffaa00' },
      ],
    },
    {
      name: 'left_arm_shield',
      voxels: [
        ...box(-3, 6, 0, 1, 4, 1, '#c8b142', { metalness: 1.0, roughness: 0.3 }),
        // Kite shield
        ...box(-4, 7, 0, 1, 4, 2, '#d4af37', { metalness: 1.0, roughness: 0.35 }),
        { x: -4, y: 8, z: 1, color: '#ffe066' },
        { x: -4, y: 9, z: 1, color: '#ffe066' },
      ],
    },
    {
      name: 'right_arm_sword_raised',
      voxels: [
        // Gauntlet
        ...box(3, 6, 0, 1, 3, 1, '#c8b142', { metalness: 1.0, roughness: 0.3 }),
        // Crossguard
        ...box(2, 9, 0, 3, 1, 1, '#aa8830', { metalness: 1.0, roughness: 0.4 }),
        // Raised glowing blade
        ...box(3, 10, 0, 1, 7, 1, '#fff4cc', {
          emissive: '#ffe066',
          metalness: 1.0,
          roughness: 0.15,
        }),
        // Pommel
        { x: 3, y: 8, z: 0, color: '#aa8830', metalness: 1.0, roughness: 0.4 },
      ],
    },
  ],
};

// ─── Dark Templar (dark_templar) ────────────────────────────
// Heavily armored dark knight: black armor plates, crimson cape, visored helm,
// shoulders broader than average. Distinct from the undead death_knight.
export const darkTemplar: CharacterPrefab = {
  name: 'Dark Templar',
  description: 'Heavily armored dark knight with crimson cape',
  maxHp: 55,
  parts: [
    {
      name: 'helm',
      voxels: [
        // Black visored helm
        ...box(-1, 11, -1, 3, 4, 3, '#2a2a2a', { metalness: 1.0, roughness: 0.4 }),
        // Visor slit — glowing red
        { x: -1, y: 12, z: 2, color: '#8b0000', emissive: '#4a0000' },
        { x: 0, y: 12, z: 2, color: '#8b0000', emissive: '#4a0000' },
        { x: 1, y: 12, z: 2, color: '#8b0000', emissive: '#4a0000' },
        // Crown spike
        { x: 0, y: 15, z: 0, color: '#1a1a1a' },
      ],
    },
    {
      name: 'pauldrons',
      // Broader-than-average shoulders
      voxels: [
        ...box(-3, 10, -1, 2, 2, 3, '#1a1a1a', { metalness: 1.0, roughness: 0.4 }),
        ...box(2, 10, -1, 2, 2, 3, '#1a1a1a', { metalness: 1.0, roughness: 0.4 }),
        // Spikes on pauldrons
        { x: -3, y: 12, z: 0, color: '#8b0000' },
        { x: 3, y: 12, z: 0, color: '#8b0000' },
      ],
    },
    {
      name: 'torso_plate',
      voxels: [
        ...box(-2, 6, -1, 5, 5, 3, '#2a2a2a', { metalness: 1.0, roughness: 0.4 }),
        // Crimson inlay down the center
        ...box(0, 6, 2, 1, 5, 1, '#8b0000'),
        // Belt
        ...box(-2, 5, 1, 5, 1, 1, '#1a1a1a'),
      ],
    },
    {
      name: 'cape_crimson',
      // Cape flowing behind — slightly off the ground
      voxels: [
        { x: -2, y: 5, z: -2, color: '#8b0000' },
        { x: -1, y: 5, z: -2, color: '#8b0000' },
        { x: 0, y: 5, z: -2, color: '#8b0000' },
        { x: 1, y: 5, z: -2, color: '#8b0000' },
        { x: 2, y: 5, z: -2, color: '#8b0000' },
        { x: -2, y: 4, z: -2, color: '#8b0000' },
        { x: -1, y: 4, z: -2, color: '#8b0000' },
        { x: 0, y: 4, z: -2, color: '#8b0000' },
        { x: 1, y: 4, z: -2, color: '#8b0000' },
        { x: 2, y: 4, z: -2, color: '#8b0000' },
        { x: -2, y: 3, z: -2, color: '#8b0000' },
        { x: -1, y: 3, z: -2, color: '#8b0000' },
        { x: 0, y: 3, z: -2, color: '#8b0000' },
        { x: 1, y: 3, z: -2, color: '#8b0000' },
        { x: 2, y: 3, z: -2, color: '#8b0000' },
        { x: -2, y: 2, z: -2, color: '#5a0000' },
        { x: -1, y: 2, z: -2, color: '#5a0000' },
        { x: 0, y: 2, z: -2, color: '#5a0000' },
        { x: 1, y: 2, z: -2, color: '#5a0000' },
        { x: 2, y: 2, z: -2, color: '#5a0000' },
      ],
    },
    {
      name: 'left_arm',
      voxels: box(-3, 6, 0, 1, 5, 1, '#2a2a2a', { metalness: 1.0, roughness: 0.4 }),
    },
    {
      name: 'right_arm_sword',
      voxels: [
        ...box(3, 6, 0, 1, 3, 1, '#2a2a2a', { metalness: 1.0, roughness: 0.4 }),
        // Crossguard
        ...box(2, 9, 0, 3, 1, 1, '#5a3a1a'),
        // Dark blade
        ...box(3, 10, 0, 1, 6, 1, '#3a3a3a', { metalness: 1.0, roughness: 0.3 }),
        // Pommel
        { x: 3, y: 8, z: 0, color: '#5a3a1a' },
      ],
    },
    {
      name: 'legs',
      voxels: [
        ...box(-2, 0, 0, 2, 5, 1, '#1a1a1a'),
        ...box(1, 0, 0, 2, 5, 1, '#1a1a1a'),
        // Sabatons
        { x: -2, y: 0, z: 1, color: '#2a2a2a' },
        { x: -1, y: 0, z: 1, color: '#2a2a2a' },
        { x: 1, y: 0, z: 1, color: '#2a2a2a' },
        { x: 2, y: 0, z: 1, color: '#2a2a2a' },
      ],
    },
  ],
};
